<template>
    <Popover v-model:open="isPopoverOpen">
        <PopoverTrigger as-child>
            <Button variant="primary" :size="props.size">
                <Icon name="add" />
                <span>{{ $t("BOMTool.AddToBOM") }}</span>
            </Button>
        </PopoverTrigger>
        <PopoverContent class="p-0">
            <ButtonGroup class="p-2">
                <input
                    v-model="newBOMName"
                    type="text"
                    class="text-sm grow"
                    :placeholder="createInputPlaceHolder"
                    :disabled="isBusy"
                    @input="isNameChanged = true"
                    @focus="createInputFocus = true"
                    @blur="createInputFocus = false"
                    @keydown.enter="addPartToNewBOM()" />
                <Button variant="accent" :disabled="!isValid || isBusy" @click="addPartToNewBOM()">
                    <Icon name="add" />
                </Button>
            </ButtonGroup>
            <div class="m-1 text-xs text-danger">{{ validationError }}</div>
            <div v-if="isBusy" class="p-1 text-sm flex items-center gap-2">
                <Spinner size="sm" />
                <span>{{ $t("BOMTool.Loading") }}</span>
            </div>
            <ul class="divide-y divide-gray-200 max-h-[33vh] overflow-y-auto text-sm">
                <li v-for="bom in sortedBOMs" :key="bom.HashId" class="block hover:bg-gray-100">
                    <div v-if="bom.PartExists" class="flex justify-between p-1.5 gap-1">
                        <span class="flex gap-1 min-w-0">
                            <span class="truncate">{{ bom.Name }}</span>
                            <i class="text-muted shrink-0">{{ $t("BOMTool.Added") }}</i>
                        </span>
                        <NuxtLink :href="localePath('/bom/' + bom.HashId)" class="shrink-0">
                            {{ $t("Global.View") }}
                        </NuxtLink>
                    </div>
                    <div
                        v-else
                        class="flex justify-between p-1.5 cursor-pointer focus:bg-gray-100 focus:outline-hidden"
                        tabindex="0"
                        @click="addPartToBOM(bom)">
                        <span class="truncate">{{ bom.Name }}</span>
                        <Spinner v-if="bom.HashId == addingHashId" size="xs" />
                    </div>
                </li>
            </ul>
        </PopoverContent>
    </Popover>
</template>
<script setup lang="ts">
import type { ButtonVariants } from "../ui";
import { safeParse } from "valibot";

const { gtag } = useGtag();
const { t } = useI18n();
const { toast } = useToast();
const localePath = useLocalePath();

const { fetchBOMs, createBOM, saveBOMPart, saveBOM } = useBom();
const { BomNameSchema } = useSchemas();

const props = defineProps<{ part: Partial<BOMSearchPart>; size: ButtonVariants["size"] }>();

const isPopoverOpen = ref(false);
const allBOMs = ref<BOMSearch[] | null>(null);
const isBusy = ref(false);
const newBOMName = ref("");
const createInputFocus = ref(false);
const isNameChanged = ref(false);
const addingHashId = ref("");

const createInputPlaceHolder = computed(() =>
    t(createInputFocus.value ? "BOMTool.BOMTitlePlaceHolder" : "BOMTool.BOMUploadTitle")
);

const parseResult = computed(() => safeParse(BomNameSchema, newBOMName.value));

const isValid = computed(() => parseResult.value.success);

const validationError = computed(() => {
    if (isValid.value || !isNameChanged.value || !Array.isArray(parseResult.value?.issues)) return "";
    return parseResult.value.issues[0].message;
});

const sortedBOMs = computed<BOMSearch[]>(() =>
    Array.isArray(allBOMs.value) ? allBOMs.value.toSorted((bomA, bomB) => bomA.Name.localeCompare(bomB.Name)) : []
);

async function onToggleOpen(isOpen: boolean) {
    if (!isOpen) return;
    isNameChanged.value = false;
    newBOMName.value = "";

    try {
        if (allBOMs.value === null) {
            isBusy.value = true;
            allBOMs.value = await fetchBOMs({
                manufacturerId: props.part.ManufacturerId,
                partNumber: props.part.PartNumberScrubbedNonMeaningful,
                requireEdit: true,
            });
        }
        gtag("event", "bom_tool_open_add_to_bom", { user_type: "Authenticated" });
    } catch (e) {
        handleError(e as Error);
    } finally {
        isBusy.value = false;
    }
}

async function addPartToNewBOM() {
    if (!isValid.value) return;
    try {
        isBusy.value = true;
        const newBOM = await createBOM(newBOMName.value, props.part);
        updateBOMs(newBOM);
        toast({ title: t("BOMTool.Success"), description: t("BOMTool.SuccessfullyAddedToBOM"), variant: "success" });
        return newBOM;
    } catch (e) {
        handleError(e as Error);
    } finally {
        isBusy.value = false;
        isPopoverOpen.value = false;
    }
}

async function addPartToBOM(bomSearch: BOMSearch) {
    try {
        if (addingHashId.value == bomSearch.HashId) return;
        addingHashId.value = bomSearch.HashId;
        const updatedBOM = await saveBOMPart(bomSearch, props.part);
        if (updatedBOM) {
            await saveBOM(updatedBOM);
            updateBOMs(updatedBOM);
            toast({
                title: t("BOMTool.Success"),
                description: t("BOMTool.SuccessfullyAddedToBOM"),
                variant: "success",
            });
            gtag("event", "bom_tool_add_to_bom", {
                manufacturer_name: props.part.ManufacturerName,
                part_number: props.part.PartNumberScrubbedNonMeaningful,
            });
        }
    } catch (e) {
        handleError(e as Error);
    } finally {
        addingHashId.value = "";
        isPopoverOpen.value = false;
    }
}

function updateBOMs(bomSearch: BOMSearch) {
    if (!Array.isArray(allBOMs.value)) {
        allBOMs.value = [bomSearch];
    } else {
        allBOMs.value = [...allBOMs.value.filter((bom) => bom.HashId != bomSearch.HashId), bomSearch];
    }
}

function handleError(error: Error) {
    toast({ title: error.message || t("Global.GenericError"), variant: "warning" });
}

watch(isPopoverOpen, onToggleOpen);
</script>
